/**
 * Which open Load Board loads already have an invoice on Pending Loads, and so
 * have been delivered.
 *
 * Titan loads must match on both the order (PO) number and the load number;
 * Avfuel loads on the load (release) number alone. Loads from any other broker
 * are left alone. Any invoice status counts: an invoice only exists once the
 * load was delivered.
 */
import { DELIVERY_STATUSES } from '../config/types';

interface BoardLoad {
  id: string;
  broker?: string;
  loadNumber?: string;
  orderNumber?: string;
  status?: string;
}

interface InvoiceRecord {
  id: string;
  loadNumber?: string | number;
  orderNumber?: string | number;
}

export interface DeliveredMatch {
  loadId: string;
  invoiceId: string;
}

// Statuses that are already settled; never touched.
const CLOSED_STATUSES: string[] = [DELIVERY_STATUSES.DELIVERED, DELIVERY_STATUSES.CANCELLED];

/** "#1105139 " and "1105139" are the same number. */
const normalize = (value: string | number | undefined | null): string =>
  String(value ?? '')
    .trim()
    .replace(/^#/, '')
    .replace(/\s+/g, '')
    .toLowerCase();

const brokerKind = (broker?: string): 'titan' | 'avfuel' | null => {
  const name = (broker || '').toLowerCase().replace(/\s+/g, '');
  if (name.includes('titan')) return 'titan';
  if (name.includes('avfuel')) return 'avfuel';
  return null;
};

export const findDeliveredLoads = (
  loads: BoardLoad[],
  invoices: InvoiceRecord[]
): DeliveredMatch[] => {
  // Index invoices once: by load number, and by order + load number.
  const byLoad = new Map<string, string>();
  const byOrderAndLoad = new Map<string, string>();
  for (const invoice of invoices) {
    const loadNumber = normalize(invoice.loadNumber);
    if (!loadNumber) continue;
    if (!byLoad.has(loadNumber)) byLoad.set(loadNumber, invoice.id);
    const orderNumber = normalize(invoice.orderNumber);
    if (orderNumber) {
      const key = `${orderNumber}|${loadNumber}`;
      if (!byOrderAndLoad.has(key)) byOrderAndLoad.set(key, invoice.id);
    }
  }

  const matches: DeliveredMatch[] = [];
  for (const load of loads) {
    if (CLOSED_STATUSES.includes(load.status || '')) continue;
    const loadNumber = normalize(load.loadNumber);
    if (!loadNumber) continue;

    const kind = brokerKind(load.broker);
    let invoiceId: string | undefined;
    if (kind === 'titan') {
      const orderNumber = normalize(load.orderNumber);
      if (orderNumber) invoiceId = byOrderAndLoad.get(`${orderNumber}|${loadNumber}`);
    } else if (kind === 'avfuel') {
      invoiceId = byLoad.get(loadNumber);
    }
    if (invoiceId) matches.push({ loadId: load.id, invoiceId });
  }
  return matches;
};
